import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from "react";

import { useRefineHistory, type RefineHistoryEntry } from "./useRefineHistory";

// Define the shape of refine operations
export type RefineOperation = {
  type: "baseline" | "erase" | "negative-space" | "crop" | "ai-refine";
  imageData: string; // Base64 image data
  metadata?: {
    dimensions: {
      x: number;
      y: number;
      width: number;
      height: number;
    };
    brushSize?: number;
    selectedColor?: string;
    removedPixels?: number;
    eligiblePixels?: number;
  };
  timestamp: number;
};

type RefineHistoryContextType = {
  // History state (deterministic snapshot model)
  entries: RefineHistoryEntry<RefineOperation>[];
  index: number;
  canUndo: boolean;
  canRedo: boolean;
  hasHistory: boolean; // true when there is at least one non-baseline snapshot

  // History operations
  pushOperation: (operation: RefineOperation) => string;
  undo: () => RefineOperation | undefined;
  redo: () => RefineOperation | undefined;
  resetToBaseline: () => RefineOperation | undefined;
  clear: () => void;
  seedBaseline: (
    imageData: string,
    dims: { x: number; y: number; width: number; height: number }
  ) => void;

  // Current state
  currentImageData?: string | undefined;
  setCurrentImageData: (imageData: string) => void;

  // Current operation for restoration context
  currentOperation?: RefineOperation | undefined;
};

const RefineHistoryContext = createContext<RefineHistoryContextType | null>(null);

type RefineHistoryProviderProps = {
  children: ReactNode;
  initialImageData?: string | undefined;
};

export function RefineHistoryProvider({ children, initialImageData }: RefineHistoryProviderProps) {
  const history = useRefineHistory<RefineOperation>(10);
  const seededRef = useRef(false);

  // Local mirrors for consumers that subscribe to context and update canvas
  const [currentImageData, setCurrentImageData] = useState<string | undefined>(initialImageData);
  const [currentOperation, setCurrentOperation] = useState<RefineOperation | undefined>(undefined);

  // Seed baseline exactly once per provider lifecycle (if provided)
  useEffect(() => {
    if (seededRef.current) return;
    if (!initialImageData) return;
    seededRef.current = true;
    history.seed({
      type: "baseline",
      imageData: initialImageData,
      timestamp: Date.now()
    });
  }, [history, initialImageData]);

  // Derive current operation and image data from history's pointer
  useEffect(() => {
    const op = history.current?.payload;
    if (!op) return;
    setCurrentOperation(op);
    setCurrentImageData(op.imageData);
  }, [history.current]);

  const pushOperation = (operation: RefineOperation): string => history.push(operation);
  const undo = () => history.undo();
  const redo = () => history.redo();
  const resetToBaseline = () => history.resetToBaseline();
  const clear = () => history.clear();

  // Simple seeding helper that seeds baseline when empty
  const seedBaseline = (
    imageData: string,
    dims: { x: number; y: number; width: number; height: number }
  ) => {
    if (history.entries.length > 0) return;
    history.seed({
      type: "baseline",
      imageData,
      metadata: { dimensions: dims },
      timestamp: Date.now()
    });
  };

  const hasHistory = useMemo(() => history.entries.length > 1, [history.entries.length]);

  const contextValue: RefineHistoryContextType = {
    entries: history.entries,
    index: history.index,
    canUndo: history.canUndo,
    canRedo: history.canRedo,
    hasHistory,
    pushOperation,
    undo,
    redo,
    resetToBaseline,
    clear,
    seedBaseline,
    currentImageData,
    setCurrentImageData,
    currentOperation
  };

  return (
    <RefineHistoryContext.Provider value={contextValue}>{children}</RefineHistoryContext.Provider>
  );
}

export function useRefineHistoryContext(): RefineHistoryContextType {
  const context = useContext(RefineHistoryContext);
  if (!context) {
    throw new Error("useRefineHistoryContext must be used within RefineHistoryProvider");
  }
  return context;
}
